var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var ComponentViewModel_1;
import 'reflect-metadata';
import { Animated, Easing } from 'react-native';
import { ReactiveSubscriptions_Key, Reactive_Key } from '../Constants';
import { IGetThemeUseCaseType, IGetLocalizationUseCaseType, IGetConfigurationUseCaseType, IGetAssetsUseCaseType, IInitApplicationUseCaseType } from '../../Domain';
import { destroyMultitons, inject, injectable } from '../../Data';
function checkBreakPointChangeNeed(width) {
    return {
        xs: width > 0,
        sm: width >= 576,
        md: width >= 768,
        lg: width >= 992,
        xl: width >= 1200,
        xxl: width >= 1400
    };
}
function loadStyle(style, breakPoint, key) {
    return breakPoint[key] ? style : null;
}
function transformMediaQuery(breakPoint) {
    return Object.keys(breakPoint).reduce((acc, key) => {
        acc[key] = (style) => loadStyle(style, breakPoint, key);
        return acc;
    }, {});
}
export function createMediaQuery(width) {
    return transformMediaQuery(checkBreakPointChangeNeed(width));
}
let ComponentViewModel = ComponentViewModel_1 = class ComponentViewModel {
    constructor(initApplication) {
        this.initApplication = initApplication;
        this.animatedValues = {};
        this.useAnimatedValue = this.useAnimatedValue.bind(this);
        this.useSimpeAnimatedValue = this.useSimpeAnimatedValue.bind(this);
        this.getAnimationValue = this.getAnimationValue.bind(this);
        this.getAnimation = this.getAnimation.bind(this);
    }
    init(callback) {
        if (!this.initApplication.getMustAutoInit()) {
            callback();
            return;
        }
        if (this.initApplication.getIsInitialed()) {
            callback();
            return;
        }
        if (this.initApplication.getIsInitializing().value) {
            this.initApplication.getIsInitializing()
                .when(value => !value)
                .then(() => callback())
                .catch(() => callback());
            return;
        }
        this.initApplication.execute()
            .then(() => callback())
            .catch(() => callback());
    }
    getSubscribers(target) {
        return (Reflect.getMetadata(ReactiveSubscriptions_Key, target) || []);
    }
    useAnimatedValue(key, value, options) {
        if (!this.animatedValues[key]) {
            const initialValue = typeof (options === null || options === void 0 ? void 0 : options.initialValue) === 'number'
                ? options === null || options === void 0 ? void 0 : options.initialValue
                : value;
            this.animatedValues[key] = {
                value: initialValue,
                animation: new Animated.Value(initialValue)
            };
        }
        if (this.animatedValues[key].value === value) {
            const promise = Promise.resolve({ finished: true });
            return {
                then: promise.then,
                value,
                animatedValue: this.animatedValues[key].animation
            };
        }
        this.animatedValues[key].value = value;
        const promise = new Promise(resolve => {
            const easing = this.animatedValues[key].value < value
                ? Easing.in(Easing.ease)
                : Easing.out(Easing.ease);
            Animated.timing(this.animatedValues[key].animation, {
                toValue: value,
                duration: (options === null || options === void 0 ? void 0 : options.duration) || 300,
                delay: options === null || options === void 0 ? void 0 : options.delay,
                easing: (options === null || options === void 0 ? void 0 : options.easing) || easing,
                useNativeDriver: !!(options === null || options === void 0 ? void 0 : options.useNativeDriver)
            }).start(result => {
                resolve(result);
            });
        });
        return {
            then: promise.then,
            value,
            animatedValue: this.animatedValues[key].animation
        };
    }
    useSimpeAnimatedValue(key, value, options) {
        return this.useAnimatedValue(key, value, options).animatedValue;
    }
    getAnimationValue(key) {
        var _a;
        return (_a = this.animatedValues[key]) === null || _a === void 0 ? void 0 : _a.value;
    }
    getAnimation(key) {
        var _a;
        return (_a = this.animatedValues[key]) === null || _a === void 0 ? void 0 : _a.animation;
    }
    loadDependenciesSync({ options, props, provider, onUpdate }) {
        provider === null || provider === void 0 ? void 0 : provider.addOwner();
        if (!provider) {
            options.dependencies = Object.assign(Object.assign({}, Object.keys(props).reduce((acc, key) => (Object.assign(Object.assign({}, acc), { [key]: undefined })), {})), options.dependencies);
        }
        else {
            if (!options.dependencies) {
                options.dependencies = {};
            }
            options.dependencies.getAppConfigurationUseCase = IGetConfigurationUseCaseType;
            options.dependencies.getThemeUseCase = IGetThemeUseCaseType;
            options.dependencies.getAssetsUseCase = IGetAssetsUseCaseType;
            options.dependencies.getLocalizationUseCase = IGetLocalizationUseCaseType;
        }
        this.onUpdate = onUpdate;
        if (!provider) {
            return {};
        }
        const deps = provider === null || provider === void 0 ? void 0 : provider.resolveManySync(options.dependencies, props);
        return deps.reduce((acc, { key, value }) => {
            acc[key] = value;
            if (!value) {
                return acc;
            }
            const observers = Reflect.getMetadata(Reactive_Key, acc[key]) || [];
            if (observers.length > 0) {
                const subscribers = this.getSubscribers(acc[key]);
                subscribers.push(onUpdate);
                Reflect.defineMetadata(ReactiveSubscriptions_Key, subscribers, acc[key]);
                if (subscribers.length > 1) {
                    return acc;
                }
                observers.forEach((propertyKey) => {
                    let originalValue = acc[key][propertyKey];
                    Object.defineProperty(acc[key], propertyKey, {
                        get: () => originalValue,
                        set: (update) => {
                            originalValue = update;
                            this.getSubscribers(acc[key]).forEach(update => update());
                            return true;
                        }
                    });
                });
                onUpdate();
            }
            return acc;
        }, {});
    }
    loadDependencies({ options, props, provider, onUpdate }) {
        provider === null || provider === void 0 ? void 0 : provider.addOwner();
        if (!provider) {
            options.dependencies = Object.assign(Object.assign({}, Object.keys(props).reduce((acc, key) => (Object.assign(Object.assign({}, acc), { [key]: undefined })), {})), options.dependencies);
        }
        else {
            if (!options.dependencies) {
                options.dependencies = {};
            }
            options.dependencies.getAppConfigurationUseCase = IGetConfigurationUseCaseType;
            options.dependencies.getThemeUseCase = IGetThemeUseCaseType;
            options.dependencies.getAssetsUseCase = IGetAssetsUseCaseType;
            options.dependencies.getLocalizationUseCase = IGetLocalizationUseCaseType;
        }
        this.onUpdate = onUpdate;
        if (!provider) {
            return Promise.resolve({});
        }
        return provider === null || provider === void 0 ? void 0 : provider.resolveMany(options.dependencies, props).then(deps => {
            return deps.reduce((acc, { key, value }) => {
                acc[key] = value;
                if (!value) {
                    return acc;
                }
                const observers = Reflect.getMetadata(Reactive_Key, acc[key]) || [];
                if (observers.length > 0) {
                    const subscribers = this.getSubscribers(acc[key]);
                    subscribers.push(onUpdate);
                    Reflect.defineMetadata(ReactiveSubscriptions_Key, subscribers, acc[key]);
                    if (subscribers.length > 1) {
                        return acc;
                    }
                    observers.forEach((propertyKey) => {
                        let originalValue = acc[key][propertyKey];
                        Object.defineProperty(acc[key], propertyKey, {
                            get: () => originalValue,
                            set: (update) => {
                                originalValue = update;
                                this.getSubscribers(acc[key]).forEach(update => update());
                                return true;
                            }
                        });
                    });
                    onUpdate();
                }
                return acc;
            }, {});
        });
    }
    getStyle(options, dependencies, window, props) {
        var _a, _b, _c;
        if (typeof options.style !== 'function') {
            return dependencies.getThemeUseCase.execute(options.style || {}, options.moduleName || '', options.componentName || '');
        }
        const theme = Object.assign({ useAnimatedValue: this.useAnimatedValue, useSimpeAnimatedValue: this.useSimpeAnimatedValue, getAnimationValue: this.getAnimationValue, getAnimation: this.getAnimation }, (_c = (_b = (_a = dependencies.getAppConfigurationUseCase) === null || _a === void 0 ? void 0 : _a.configuration.value) === null || _b === void 0 ? void 0 : _b.application) === null || _c === void 0 ? void 0 : _c.theme);
        const mediaQuery = createMediaQuery(window.width);
        const style = options.style(theme, mediaQuery, window, props);
        return dependencies.getThemeUseCase.execute(style, options.moduleName || '', options.componentName || '');
    }
    destroyDependencies({ provider, dependenciesMap, dimensionsSubscriber }) {
        const dependencies = Object.values(dependenciesMap || {});
        dependencies.forEach(instance => {
            const subscribers = this.getSubscribers(instance).filter(x => x !== this.onUpdate);
            Reflect.defineMetadata(ReactiveSubscriptions_Key, subscribers, instance);
        });
        ComponentViewModel_1.destroyMultitons(dependencies);
        provider === null || provider === void 0 ? void 0 : provider.removeOwner();
        dimensionsSubscriber === null || dimensionsSubscriber === void 0 ? void 0 : dimensionsSubscriber.remove();
    }
};
ComponentViewModel.createMediaQuery = createMediaQuery;
ComponentViewModel.destroyMultitons = destroyMultitons;
ComponentViewModel = ComponentViewModel_1 = __decorate([
    injectable(),
    __param(0, inject(IInitApplicationUseCaseType)),
    __metadata("design:paramtypes", [Object])
], ComponentViewModel);
export { ComponentViewModel };
